import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { firestore } from '@config/firebase';
import { Property } from '../types/property.types';
import { Match } from '../types/match.types';
import { getErrorMessage } from '@utils/errors';
import { fitsPairBudget } from '@utils/budget';

interface UsePropertyRecommendationsProps {
  match: Match | null | undefined;
  maxResults?: number;
  includeViewed?: boolean; // New option to show viewed properties
}

export const usePropertyRecommendations = ({ match, maxResults = 10, includeViewed = false }: UsePropertyRecommendationsProps) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!match) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Build query based on match preferences
        const propertiesRef = collection(firestore, 'properties');
        
        // Simplified query - just filter by status and city
        // We'll filter by budget in memory to avoid complex index requirements
        const q = query(
          propertiesRef,
          where('status', '==', 'available'),
          where('address.city', '==', match.sharedCity),
          orderBy('createdAt', 'desc'),
          limit(100) // Fetch more, then filter in memory
        );

        const snapshot = await getDocs(q);
        let fetchedProperties = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Property[];

        // Filter by budget in memory. Roommates split one rent between the two
        // of them, so a place fits when each person's share is affordable —
        // comparing the full rent would hide shared homes that are easily
        // affordable once split. See fitsPairBudget for the model.
        fetchedProperties = fetchedProperties.filter(prop =>
          fitsPairBudget(prop.monthlyRent, match.budgetOverlap)
        );

        // Filter properties that haven't been viewed yet (unless includeViewed is true)
        const unseenProperties = includeViewed 
          ? fetchedProperties 
          : fetchedProperties.filter(prop => !match.viewedProperties?.includes(prop.id));

        // Calculate compatibility score for each property
        const scoredProperties = unseenProperties.map(property => {
          let score = 0;
          
          // Budget fit (40 points)
          if (match.budgetOverlap) {
            const budgetMid = (match.budgetOverlap.min + match.budgetOverlap.max) / 2;
            const priceDiff = Math.abs(property.monthlyRent - budgetMid);
            const maxDiff = match.budgetOverlap.max - match.budgetOverlap.min;
            score += Math.max(0, 40 - (priceDiff / maxDiff) * 40);
          }
          
          // Amenities match (30 points)
          const sharedAmenities = property.amenities.filter(amenity =>
            match.sharedLifestyle.some(pref => 
              amenity.toLowerCase().includes(pref.toLowerCase()) ||
              pref.toLowerCase().includes(amenity.toLowerCase())
            )
          );
          score += (sharedAmenities.length / Math.max(property.amenities.length, 1)) * 30;
          
          // Occupancy fit (20 points)
          if (property.maxOccupants >= 2) {
            score += 20;
          } else if (property.maxOccupants === 1) {
            score += 5; // Might still work if they're a couple
          }
          
          // Recency (10 points)
          const daysSinceCreated = (Date.now() - property.createdAt.toMillis()) / (1000 * 60 * 60 * 24);
          score += Math.max(0, 10 - daysSinceCreated);

          return { ...property, matchScore: Math.round(score) };
        });

        // Sort by compatibility score
        scoredProperties.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));

        // Limit results
        setProperties(scoredProperties.slice(0, maxResults));
      } catch (err: unknown) {
        console.error('❌ Error fetching property recommendations:', err);
        setError(getErrorMessage(err, 'Failed to load recommendations'));
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [match, maxResults, includeViewed]);

  return { properties, loading, error };
};
